import React, {Suspense, lazy, useEffect, useMemo, useRef, useState} from "react"
import {useIntl} from "react-intl"
import {useDispatch, useSelector} from "react-redux"
import {
  getCoursesDetails,
  getPostsDetails,
} from "../../Redux/PostsDetailsReducer"
import {TitleHeader} from "../../sharedComponents/Title"
import {openLink} from "../../utils"
import Category from "../../sharedComponents/Categories"
import Tags from "../../sharedComponents/Tags"
import SidebarCourseSection from "../Sections/SidebarCourseSection"
import SidebarEventSection from "../Sections/SidebarEventSection"
import SidebarPostSection from "../Sections/SidebarPostSection"
import NewsletterSide from "../../sharedComponents/NewsLetter/NewsletterSide"
import SkeletonLoader from "../../sharedComponents/SkeletonLoader/SkeletonLoader"

const DeadlinesListSection = lazy(() =>
  import("../Sections/DeadlinesListSection")
)

const SideBar = ({
  sections,
  pageName,
  relatedPosts,
  relatedEvents,
  type,
  navId,
}) => {
  const intl = useIntl()
  const generalDetails = useSelector(
    (state) => state?.generalDetails?.generalDetails
  )
  const campaigns = useSelector((state) => state.auth.campaignDetails)
  const data = useSelector((state) => state.pageDetails.pages)
  const dispatch = useDispatch()
  const [visibleSections, setVisibleSections] = useState([])
  const observer = useRef(null)

  const memoizedCampaigns = useMemo(() => campaigns, [campaigns])

  const handleIntersection = (entries) => {
    setVisibleSections((prev) => {
      let updatedSections = [...prev]
      entries.forEach((entry) => {
        const index = entry.target.dataset.index
        if (entry.isIntersecting && !updatedSections.includes(index)) {
          updatedSections.push(index)
        }
      })
      return updatedSections
    })
  }

  useEffect(() => {
    if (sections && sections?.length > 0 && pageName) {
      observer.current = new IntersectionObserver(handleIntersection, {
        rootMargin: "-200px 0px 0px 0px",
        threshold: 0.1,
      })

      const sectionElements = document.querySelectorAll(".sidebarWindow")
      sectionElements.forEach((el) => observer.current.observe(el))

      return () => {
        if (observer.current) {
          observer.current.disconnect()
        }
      }
    }
  }, [sections, pageName])

  useEffect(() => {
    if (sections && sections.length > 0) {
      sections.forEach((ele) => {
        if (ele.type === "post-list" || ele.type === "event-list") {
          const paramsObject = {
            offset: ele.offset,
            option_for_featured: ele.option_for_featured,
            pagination: ele.pagination,
            items_per_page: ele.items_per_page,
            groups: ele.groups,
            groups_condition: ele.groups_condition,
            categories: ele.categories,
            categories_condition: ele.categories_condition,
            tags: ele.tags,
            tags_condition: ele.tags_condition,
            rule_type: ele.rule_type,
            type: ele.type,
            sorting: ele.sorting,
            disable_latest_record: ele.disable_latest_record,
          }
          if (ele.type === "post-list" || ele.type === "event-list") {
            dispatch(
              getPostsDetails({
                paramsObject,
                pageName,
                id: ele.id,
                type: ele.type,
                defaultView: ele.default_view,
                title: "sideBar",
                sectionId: ele.section_id,
              })
            )
          }
        } else if (ele.type === "course-list") {
          const paramsObject = {
            offset: ele.offset,
            option_for_featured: ele.option_for_featured,
            total_items: ele.total_items,
            items_per_page: ele.items_per_page,
            sorting: parseInt(ele.sorting),
            groups: ele.groups,
            groups_condition: ele.groups_condition,
            categories: ele.categories,
            categories_condition: ele.categories_condition,
            tags: ele.tags,
            tags_condition: ele.tags_condition,
            rule_type: ele.rule_type,
            type: ele.type,
            disable_latest_record: ele.disable_latest_record,
          }
          dispatch(
            getCoursesDetails({
              paramsObject,
              id: ele.id,
              pageName,
              type: ele.type,
              title: ele.title,
              sectionId: ele.section_id,
            })
          )
        }
      })
    }
    //eslint-disable-next-line
  }, [pageName, sections])

  const getDynamicMinHeight = (type) => {
    if (type === "NewsLetter") {
      return "220px"
    } else if (type === "deadlines-list") {
      return "350px"
    } else {
      return "55px"
    }
  }

  return (
    <div className="mt-0">
      {sections && sections.length > 0
        ? sections.map((element, index) => {
            const isVisible = visibleSections.includes(index.toString())
            const uId = `${element?.section_id}_${element?.id}`
            return (
              <div
                key={index}
                className="sidebarWindow"
                data-index={index}
                style={{minHeight: getDynamicMinHeight(element.type)}}
              >
                {isVisible && element.type === "NewsLetter" ? (
                  <NewsletterSide
                    key={index}
                    type={type}
                    details={element}
                    sidebars={true}
                    isSidebar={true}
                    pageName={pageName}
                    generalDetails={generalDetails}
                    pageDetails={data}
                    navId={navId}
                  />
                ) : element.type === "deadlines-list" ? (
                  <Suspense
                    fallback={<SkeletonLoader type="deadlines-list-side" />}
                  >
                    <DeadlinesListSection
                      key={index}
                      id={element.id}
                      title={element.title}
                      pageName={pageName}
                      isSidebar={true}
                      details={element}
                    />
                  </Suspense>
                ) : element.type === "category-list" ? (
                  <Category
                    details={element}
                    key={index}
                    title="category"
                    pageName={pageName}
                  />
                ) : element.type === "group-list" ? (
                  <Category
                    details={element}
                    key={index}
                    title="groups"
                    pageName={pageName}
                  />
                ) : element.type === "tag-list" ? (
                  <Tags
                    details={element}
                    key={index}
                    pageName={pageName}
                    title={element.title}
                  />
                ) : element.type === "post-list" ? (
                  <SidebarPostSection
                    displayHeader={element?.display_header}
                    key={index}
                    uId={uId}
                    pageName={pageName}
                    title={element.title}
                    type="post-list"
                    ele={element}
                  />
                ) : element.type === "event-list" ? (
                  <SidebarEventSection
                    displayHeader={element?.display_header}
                    key={index}
                    uId={uId}
                    pageName={pageName}
                    title={element.title}
                    type="event-list"
                    ele={element}
                  />
                ) : element.type === "course-list" ? (
                  <SidebarCourseSection
                    displayHeader={element?.display_header}
                    key={index}
                    uId={uId}
                    pageName={pageName}
                    title={element.title}
                    type="course-list"
                    ele={element}
                  />
                ) : element?.section_id === 10 ? (
                  memoizedCampaigns?.sidebarBannerCampaign?.image_url && (
                    <div
                      className="mb-3"
                      title={memoizedCampaigns?.sidebarBannerCampaign?.name}
                      style={{cursor: "pointer"}}
                    >
                      <TitleHeader
                        title={intl.formatMessage({id: "TITLE_ADVERTISE"})}
                        noMarginTop
                        header_navigation_id={element?.header_navigation_id}
                      />
                      <div
                        className=""
                        onClick={() => {
                          if (
                            memoizedCampaigns?.sidebarBannerCampaign?.site_url
                          )
                            openLink(
                              memoizedCampaigns?.sidebarBannerCampaign?.site_url
                            )
                        }}
                      >
                        {memoizedCampaigns?.sidebarBannerCampaign
                          ?.image_url && (
                          <img
                            className="rounded web-kit-width w-100"
                            src={
                              memoizedCampaigns?.sidebarBannerCampaign
                                ?.image_url
                            }
                            alt=""
                          />
                        )}
                      </div>
                    </div>
                  )
                ) : null}
              </div>
            )
          })
        : null}
    </div>
  )
}

export default SideBar
